import 'tippy.js/dist/tippy.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'rc-slider/assets/index.css';
import 'rc-dialog/assets/index.css';
import '@hangar31/rc-image-cropper/dist/cropper.css';
import '@hangar31/rc-modal/dist/index.css';
import 'quill/dist/quill.snow.css'; // Add css for snow theme
import GlobalStyled from '@OnArk/styles/Global.styled';

import React, { ReactElement, ReactNode, useEffect } from 'react';

import {
  AppPropsType,
  NextComponentType,
  NextPageContext,
} from 'next/dist/shared/lib/utils';
import { useRouter } from 'next/router';

import { ThemeProvider } from '@emotion/react';
import HeadMeta from '@OnArk/global/HeadMeta';
import HeadScripts from '@OnArk/global/HeadScripts';
import LayoutWrapper from '@OnArk/global/Layout';
import {
  fallbackFeatureFlagSnapshot,
  type FeatureFlagSnapshot,
} from '@onark/observability';
import type { FunnelClass } from '@onark/shared/welcomeAboardFunnelClass';
import { useFirstTouchAttribution } from '@OnArk/utils/attribution';
import { useBeacon } from '@OnArk/utils/beacon';
import { usePostHog } from '@OnArk/utils/posthog';
import { Layout } from 'graphql/withAuth';

import { FeatureFlagProvider } from '../app/_components/FeatureFlags';
import theme, { OnArkTheme } from '../theme';
// The fields _app itself reads (all optional — pages without withAuth ship
// none of them), plus an `unknown` passthrough for every page's own props.
// `client` isn't in SSR props at all (withClient injects it at render time),
// so it's optional here purely so page components typed with it still accept
// the spread below.
type PageProps = { children?: ReactNode } & {
  billing?: { plan?: string | null; status?: string | null }[];
  checkIn?: WithAuthCheckIn;
  church?: WithAuthChurch;
  client?: Sdk;
  featureFlags?: FeatureFlagSnapshot;
  // The guest check-in page's server-computed "is this one of OnArk's own
  // funnel classes" — read here only to put the Meta pixel on that page.
  funnelClass?: FunnelClass | null;
  user?: WithAuthUser;
  [key: string]: unknown;
};

interface LayoutProps {
  layout: Layout;
  pageDescription: string;
  pageTitle: string;
}

// The generated SDK types the church color scalars (`bannerColor` & co.) as
// `any`. At runtime they're CSS color strings (hex/rgb/JSON) or null, while
// theme.ts declares parsed-Color-object defaults — both are valid toColor()
// inputs, so the theme's declared object type is narrower than reality.
// Assert each scalar to the theme field's declared type in this one place so
// the rest of the app stays typed without `any` leaking out.
type ChurchThemeColor = OnArkTheme['church']['bannerColor'] | null | undefined;

function buildThemeWithChurch(church: WithAuthChurch | undefined): OnArkTheme {
  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing --
     intentional `||` on the color fields: a church color can be stored as an
     empty string, which must fall back to the theme default like null does. */
  return {
    ...theme,
    church: {
      ...theme.church,
      bannerColor:
        (church?.bannerColor as ChurchThemeColor) || theme.church.bannerColor,
      bannerStyle: church?.bannerStyle || theme.church.bannerStyle,
      buttonColor:
        (church?.buttonColor as ChurchThemeColor) || theme.church.buttonColor,
      buttonStyle: church?.buttonStyle || theme.church.buttonStyle,
      buttonTextColor:
        (church?.buttonTextColor as ChurchThemeColor) ||
        theme.church.buttonTextColor,
    },
  };
  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
}

function findLiveChat(church: WithAuthChurch | undefined) {
  return church?.integrations?.find(i => i.type === 'LIVE_CHAT');
}

function MyApp({
  Component,
  pageProps: { layout, pageDescription, pageTitle, ...pageProps },
}: {
  pageProps: PageProps & LayoutProps;
  Component: NextComponentType<NextPageContext, unknown, PageProps>;
} & Omit<AppPropsType, 'pageProps' | 'Component'>): ReactElement {
  const themeWithChurch = buildThemeWithChurch(pageProps.church);
  const liveChat = findLiveChat(pageProps.church);

  usePostHog();
  useFirstTouchAttribution();
  useBeacon({
    church: pageProps.church,
    layout,
    // BUG SURFACED BY TYPING (fixed): `billing` is an ARRAY of subscription
    // summaries (see withAuth), but this previously read `billing?.plan` —
    // `.plan` on an array — so Beacon's "Plan" session data was ALWAYS
    // undefined. Read the first subscription's plan instead. (For super users
    // billing can list other churches' subs, but the first entry matches the
    // pre-existing "some plan" intent better than never sending one.)
    plan: pageProps.billing?.[0]?.plan ?? undefined,
    user: pageProps.user,
  });

  // Fire GA4 page_view + Meta Pixel PageView on client-side route changes.
  // Without this, Next.js SPA navigation (e.g. /start/pco → /sign-up) only
  // tracks the first page in a session, breaking funnel attribution and
  // retargeting audiences built on page_location.
  const router = useRouter();

  useEffect(() => {
    function handleRouteChange(url: string) {
      if (typeof window === 'undefined') return;
      const gtag = window.gtag;

      if (typeof gtag === 'function') {
        gtag('event', 'page_view', {
          page_location: window.location.href,
          page_path: url,
          page_title: document.title,
        });
      }
      const fbq = window.fbq;

      if (typeof fbq === 'function') {
        fbq('track', 'PageView');
      }
    }
    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    function _calculateScrollbarWidth() {
      document.documentElement.style.setProperty(
        '--scrollbar-width',
        `${window.innerWidth - document.documentElement.clientWidth}px`
      );
      document.documentElement.style.setProperty(
        '--vw',
        `${document.documentElement.clientWidth}px`
      );
    }
    if (typeof window !== 'undefined') {
      // recalculate on resize
      window.addEventListener('resize', _calculateScrollbarWidth, false);
      // recalculate on dom load
      document.addEventListener(
        'DOMContentLoaded',
        _calculateScrollbarWidth,
        false
      );
      // recalculate on load (assets loaded as well)
      window.addEventListener('load', _calculateScrollbarWidth);
    }

    return () => {
      window.removeEventListener('resize', _calculateScrollbarWidth, false);
      document.removeEventListener(
        'DOMContentLoaded',
        _calculateScrollbarWidth,
        false
      );
      window.removeEventListener('load', _calculateScrollbarWidth);
    };
  }, []);

  return (
    // The snapshot withAuth resolved server-side, handed to the same provider
    // the App Router uses so both trees answer `useFeatureFlag` identically.
    // Pages without withAuth pass nothing and land on the declared fallbacks.
    <FeatureFlagProvider
      flags={pageProps.featureFlags ?? fallbackFeatureFlagSnapshot()}
    >
      <ThemeProvider theme={themeWithChurch}>
        <GlobalStyled theme={themeWithChurch} />
        {process.env.NODE_ENV === 'development' && (
          // Next's dev error-overlay portal intercepts pointer events across
          // the WHOLE page whenever any warning has fired (legacy components
          // emit several; tracked to fix at source) — silently eating clicks
          // and hover (e.g. the video player's controls). Its shadow content
          // re-enables pointer-events, so hide the host outright in dev (same
          // approach as the demo recorder). Errors still land in the console
          // and terminal.
          <style>{'nextjs-portal { display: none !important; }'}</style>
        )}

        <HeadMeta pageDescription={pageDescription} pageTitle={pageTitle} />
        <HeadScripts
          funnelClass={pageProps.funnelClass}
          layout={layout}
          user={pageProps.user}
        />

        {layout === 'GUEST' && liveChat && (
          <div
            dangerouslySetInnerHTML={{
              // `settings` is a Json scalar (`any` in the generated SDK); for
              // LIVE_CHAT integrations it's `{ code: string }` — the embed
              // snippet pasted by the church admin.
              __html:
                (liveChat.settings as { code?: string } | null | undefined)
                  ?.code ?? '',
            }}
          />
        )}

        <LayoutWrapper layout={layout} pageProps={pageProps}>
          <Component {...pageProps} />
        </LayoutWrapper>
      </ThemeProvider>
    </FeatureFlagProvider>
  );
}

export default MyApp;
